import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';
import { SmartLinkStats } from 'fym-shared/src/smartlinks/stats';
import { SerializedWidget } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../common/types';
import { SMLStatCards } from './SMLStatCards';
import { SMLClicksChart } from './SMLClicksChart';
import { SMLBreakdownPanel } from './SMLBreakdownPanel';
import { SMLBlocksPanel } from './SMLBlocksPanel';
import { formatServiceName } from './formatServiceName';
import { formatCountryName } from './formatCountryName';
import { SMLMediaPanel } from './media/SMLMediaPanel';
import { hasVideoBlock, overallWatchedPct } from './media/mediaStatsRows';

interface SMLStatsPanelsProps {
  stats: SmartLinkStats;
  days: number;
  /** Present on a single SmartLink, absent for a whole account. */
  widgets?: SerializedWidget[] | null;
  /** The three campaign-only touches, all off for a plain SmartLink. */
  presaveCount?: number;
  hasCampaign?: boolean;
  emptyChart?: React.ReactNode;
  /** False for a page that is not published. No value: the account view. */
  isLive?: boolean;
}

/** The full analytics layout, shared by the Smartlinks index and a SmartLink. */
const SMLStatsPanelsComponent: React.FC<SMLStatsPanelsProps> = ({
  stats,
  days,
  widgets,
  presaveCount,
  hasCampaign,
  emptyChart,
  isLive,
}) => {
  const intl = useIntl();
  const showsPlays = hasVideoBlock(widgets) || (stats.totals.mediaPlays ?? 0) > 0;
  const videoPlays = useMemo(
    () => (showsPlays ? { watchedPct: overallWatchedPct(stats.media) } : undefined),
    [showsPlays, stats.media]
  );

  return (
    <>
      <SMLStatCards
        totals={stats.totals}
        presaveCount={presaveCount}
        hasCampaign={hasCampaign}
        videoPlays={videoPlays}
      />
      <SMLClicksChart series={stats.series} days={days} empty={emptyChart} />
      {widgets !== undefined && (
        <SMLMediaPanel items={stats.media} widgets={widgets} pageViews={stats.totals.views} days={days} />
      )}
      <Row className="g-3">
        <Col xs={12} lg={6}>
          <SMLBreakdownPanel
            title={intl.formatMessage({ id: 'app.sml.stats.services', defaultMessage: 'Where they listened' })}
            items={stats.services}
            formatKey={formatServiceName}
          />
        </Col>
        <Col xs={12} lg={6}>
          <SMLBreakdownPanel
            title={intl.formatMessage({ id: 'app.sml.stats.markets', defaultMessage: 'Markets' })}
            items={stats.countries}
            formatKey={(key) => formatCountryName(intl, key)}
          />
        </Col>
      </Row>
      <SMLBlocksPanel blocks={stats.blocks} widgets={widgets} isLive={isLive} />
    </>
  );
};

export const SMLStatsPanels = TypedMemo(SMLStatsPanelsComponent);
